import { useEffect, useState } from 'react';
import api from '../utils/api';
import store from '../utils/store';
import LoadingPage from './LoadingPage';
import analytics from '../utils/analytics';
import NotificationToast from '../components/NotificationToast';
import ErrorAlert from '../components/ErrorAlert';
import Skeleton from '../components/Skeleton';
import RotateApiKeyModal from '../components/RotateApiKeyModal';

export default function Profile() {
  const [isLoading, setIsLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingBilling, setIsSavingBilling] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [billingSuccess, setBillingSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [billingError, setBillingError] = useState('');
  const [userData, setUserData] = useState(null);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [rotateSuccess, setRotateSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const accessToken = store.getItem('accessToken');

  useEffect(() => {
    async function loadUserData() {
      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      try {
        // Try to get dashboard data first for the navbar
        let dashboardData;
        try {
          dashboardData = await api.retrieveDashboardData(accessToken);
        } catch (dashboardError) {
          console.error('Error loading dashboard data:', dashboardError);
        }
        
        // Fetch user profile using the profile endpoint
        const profileData = await api.getUserProfile(accessToken);
        
        // Merge dashboard data with profile data
        const formattedData = {
          ...dashboardData,
          ...profileData,
          email: profileData.email || dashboardData?.email,
          id: profileData.id || dashboardData?.id
        };
        setUserData(formattedData);
        
        // Set initial values from user data
        setFirstName(profileData.first_name || '');
        setLastName(profileData.last_name || '');
        setBillingEmail(profileData.billing_email || '');
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadUserData();
  }, [accessToken]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setIsSavingProfile(true);

    try {
      // API call to update profile - you'll need to add this endpoint to api.js
      await api.updateUserProfile(accessToken, {
        first_name: firstName,
        last_name: lastName
      });
      
      setProfileSuccess(true);
      analytics.track('Profile Updated', {
        first_name: !!firstName,
        last_name: !!lastName
      });
      
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (error) {
      setProfileError(error.message);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setBillingError('');
    setIsSavingBilling(true);

    try {
      await api.updateBillingEmail(accessToken, billingEmail);
      
      // Update the local state with the new billing email
      setUserData(prev => ({ ...prev, billing_email: billingEmail }));
      setBillingSuccess(true);
      analytics.track('Billing Email Updated');
      
      setTimeout(() => setBillingSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating billing email:', error);
      setBillingError(error.message || 'Failed to update billing email');
      // Ensure we don't lose the form data on error
      setIsSavingBilling(false);
    } finally {
      setIsSavingBilling(false);
    }
  };

  const handleRemoveBillingEmail = async () => {
    if (!window.confirm('Are you sure you want to remove your billing email?')) {
      return;
    }
    
    setBillingError('');
    setIsSavingBilling(true);

    try {
      await api.removeBillingEmail(accessToken);
      
      setBillingEmail('');
      setUserData(prev => ({ ...prev, billing_email: null }));
      setBillingSuccess(true);
      analytics.track('Billing Email Removed');
      
      setTimeout(() => setBillingSuccess(false), 3000);
    } catch (error) {
      setBillingError(error.message);
    } finally {
      setIsSavingBilling(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setIsSavingPassword(true);

    try {
      await api.changePassword(accessToken, currentPassword, newPassword, confirmPassword);

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess(true);
      analytics.track('Password Changed');

      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      setPasswordError(error.message.split('|'));
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleApiKeyRotated = (newToken) => {
    setUserData(prev => ({ ...prev, token: newToken }));
    setRotateSuccess(true);
    analytics.track('API Key Rotated');

    setTimeout(() => setRotateSuccess(false), 3000);
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <Skeleton dashboard={userData || {}}>
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Account Settings</h1>
          
          <div className="space-y-12">
            {/* Profile Section */}
            <div className="bg-white shadow-sm rounded-lg">
              <form onSubmit={handleProfileSubmit}>
                <div className="px-6 py-8 sm:p-8">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Update your personal information.
                    </p>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label htmlFor="first-name" className="block text-sm font-medium text-gray-900">
                        First name
                      </label>
                      <div className="mt-2">
                        <input
                          id="first-name"
                          name="first-name"
                          type="text"
                          autoComplete="given-name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-mypurple sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <label htmlFor="last-name" className="block text-sm font-medium text-gray-900">
                        Last name
                      </label>
                      <div className="mt-2">
                        <input
                          id="last-name"
                          name="last-name"
                          type="text"
                          autoComplete="family-name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-mypurple sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {profileError && (
                    <div className="mt-4">
                      <ErrorAlert msg={profileError} />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-x-4 px-6 py-4 sm:px-8 border-t border-gray-200 rounded-b-lg">
                  <button
                    type="submit"
                    disabled={isSavingProfile}
                    className="rounded-md bg-mypurple px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-mypurple/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mypurple disabled:opacity-50"
                  >
                    {isSavingProfile ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>

            {/* Billing Details Section */}
            <div className="bg-white shadow-sm rounded-lg">
              <form onSubmit={handleBillingSubmit}>
                <div className="px-6 py-8 sm:p-8">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Billing Details</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Update your billing email address for invoices and receipts.
                    </p>
                  </div>

                  <div className="mt-6 max-w-lg">
                    <label htmlFor="billing-email" className="block text-sm font-medium text-gray-900">
                      Billing email
                    </label>
                    <div className="mt-2">
                      <input
                        id="billing-email"
                        name="billing-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={billingEmail}
                        onChange={(e) => setBillingEmail(e.target.value)}
                        className="block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-mypurple sm:text-sm"
                        placeholder="billing@company.com"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      All invoices and billing-related emails will be sent to this address.
                    </p>
                  </div>

                  {billingError && (
                    <div className="mt-4">
                      <ErrorAlert msg={billingError} />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-x-4 px-6 py-4 sm:px-8 border-t border-gray-200 rounded-b-lg">
                  {userData?.billing_email && (
                    <button
                      type="button"
                      onClick={handleRemoveBillingEmail}
                      disabled={isSavingBilling}
                      className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-50 disabled:opacity-50"
                    >
                      Remove Billing Email
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isSavingBilling}
                    className="rounded-md bg-mypurple px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-mypurple/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mypurple disabled:opacity-50"
                  >
                    {isSavingBilling ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>

            {/* Change Password Section */}
            <div className="bg-white shadow-sm rounded-lg">
              <form onSubmit={handlePasswordSubmit}>
                <div className="px-6 py-8 sm:p-8">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Update the password you use to sign in. You will need to enter your current
                      password to confirm the change. You will be signed out of all other sessions.
                    </p>
                  </div>

                  <div className="mt-6 grid max-w-lg grid-cols-1 gap-y-6">
                    <div className="col-span-full">
                      <label htmlFor="current-password" className="block text-sm font-medium text-gray-900">
                        Current password
                      </label>
                      <div className="mt-2">
                        <input
                          id="current-password"
                          name="current-password"
                          type="password"
                          autoComplete="current-password"
                          required
                          value={currentPassword}
                          onChange={(e) => {
                            setCurrentPassword(e.target.value);
                            setPasswordError('');
                          }}
                          className="block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-mypurple sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="col-span-full">
                      <label htmlFor="new-password" className="block text-sm font-medium text-gray-900">
                        New password
                      </label>
                      <div className="mt-2">
                        <input
                          id="new-password"
                          name="new-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            setPasswordError('');
                          }}
                          className="block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-mypurple sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="col-span-full">
                      <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-900">
                        Confirm new password
                      </label>
                      <div className="mt-2">
                        <input
                          id="confirm-password"
                          name="confirm-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            setPasswordError('');
                          }}
                          className="block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-mypurple sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {passwordError && (
                    <div className="mt-4">
                      <ErrorAlert msg={passwordError} />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-x-4 px-6 py-4 sm:px-8 border-t border-gray-200 rounded-b-lg">
                  <button
                    type="submit"
                    disabled={isSavingPassword}
                    className="rounded-md bg-mypurple px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-mypurple/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mypurple disabled:opacity-50"
                  >
                    {isSavingPassword ? 'Updating...' : 'Update password'}
                  </button>
                </div>
              </form>
            </div>

            {/* Rotate API Key Section */}
            <div className="bg-white shadow-sm rounded-lg">
              <div className="px-6 py-8 sm:p-8">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Rotate API Key</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Generate a new API key for your account. Your current key will stop working
                    immediately, and any integrations using it will need to be updated with the
                    new key. You will be asked to confirm this action with your password.
                  </p>
                </div>

                <div className="mt-6 flex items-start">
                  <button
                    type="button"
                    onClick={() => setShowRotateModal(true)}
                    className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                  >
                    Rotate API key
                  </button>
                </div>
              </div>
            </div>

            {/* Placeholder for Teams Section */}
            <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg px-6 py-12 text-center">
              <h3 className="text-lg font-medium text-gray-900">Teams & Members</h3>
              <p className="mt-2 text-sm text-gray-600">Team management features coming soon</p>
            </div>
          </div>
        </div>
      </div>

      {profileSuccess && (
        <NotificationToast
          notifMsg="Profile updated successfully!"
          show={profileSuccess}
          setShow={setProfileSuccess}
          notifType="success"
        />
      )}

      {billingSuccess && (
        <NotificationToast
          notifMsg="Billing email updated successfully!"
          show={billingSuccess}
          setShow={setBillingSuccess}
          notifType="success"
        />
      )}

      {passwordSuccess && (
        <NotificationToast
          notifMsg="Password updated successfully!"
          show={passwordSuccess}
          setShow={setPasswordSuccess}
          notifType="success"
        />
      )}

      {rotateSuccess && (
        <NotificationToast
          notifMsg="API key rotated successfully!"
          show={rotateSuccess}
          setShow={setRotateSuccess}
          notifType="success"
        />
      )}

      <RotateApiKeyModal
        open={showRotateModal}
        setOpen={setShowRotateModal}
        email={userData?.email}
        onRotated={handleApiKeyRotated}
      />
    </Skeleton>
  );
}